import { ROLE_IDS } from "../../../constants/roles.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { type PermissionFeatures } from "../../SettingsPermissions/types/permissions.types.ts"

export type AutomationSection = {
  key: "communication" | "status" | "requiredInformation"
  route: string
  labelKey: string
}

// One source for both the top-level Automation tab and its sub-tab strip, so
// the tab never opens on a section the strip hides.
export function useAutomationSections(
  permissions?: PermissionFeatures
): AutomationSection[] {
  const storedPermissions = useAppSelector(
    (state) => state.auth?.userInfo?.permissions?.codes
  )
  const codes = permissions ?? storedPermissions
  const roleId = useAppSelector((state) => state.auth?.userInfo?.role?.id)
  const leadAutomationOn = useAppSelector(
    (state) => !!state.auth?.userInfo?.feature_lead_automation
  )
  // Read like `selectIsOwner`, tolerating a store without the branches slice.
  const isOwner = useAppSelector((state) => !!state.branches?.isOwner)

  const sections: [AutomationSection, boolean][] = [
    [
      {
        key: "communication",
        route: RoutesPaths.settings.automation,
        labelKey: "settingsAutomation.sections.communication"
      },
      leadAutomationOn &&
        !!codes?.settings?.automations?.settings_automation_view
    ],
    [
      {
        key: "status",
        route: RoutesPaths.settings.statusAutomation,
        labelKey: "settingsAutomation.sections.status"
      },
      canManageStatusAutomation(roleId, isOwner)
    ],
    [
      {
        key: "requiredInformation",
        route: RoutesPaths.settings.formFieldRequirements,
        labelKey: "settingsAutomation.sections.requiredInformation"
      },
      !!codes?.settings?.form_requirements?.form_requirements_manage
    ]
  ]

  return sections.filter(([, visible]) => visible).map(([section]) => section)
}

// Company-wide, so the same people who reached it under Configurations at
// HQ; managing a branch is no reason to open a setting every branch shares.
export const canManageStatusAutomation = (
  roleId: number | undefined,
  isOwner: boolean | undefined
) =>
  !!isOwner ||
  roleId === ROLE_IDS.ADMIN_GCC ||
  roleId === ROLE_IDS.ADMIN_PLATFORM
